import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import toast from "react-hot-toast";
import { api, getErrorMessage } from "../../../api/client.ts";
import { useAuth } from "../../../contexts/AuthContext.tsx";

/** Turning two-step verification off takes an authenticator or backup code. Mounted only while open. */
export default function DisableMfaDialog({ onClose }: { onClose: () => void }) {
  const { refreshUser } = useAuth();
  const [code, setCode] = useState("");

  const disable = useMutation({
    mutationFn: () => api.mfaDisable(code.trim()),
    meta: { suppressErrorToast: true },
    onSuccess: async () => {
      await refreshUser();
      toast.success("Two-step verification is off");
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.trim()) disable.mutate();
  };

  return (
    <Dialog open onClose={disable.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>Turn off two-step verification</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Signing in will only ask for your password. Your backup codes are deleted too.
            </Typography>
            {disable.isError && <Alert severity="error">{getErrorMessage(disable.error)}</Alert>}
            <TextField
              label="Authenticator or backup code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
              size="small"
              autoComplete="one-time-code"
              slotProps={{ htmlInput: { maxLength: 16, style: { fontFamily: "monospace", letterSpacing: "0.1em" } } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={disable.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="error" loading={disable.isPending} disabled={!code.trim()}>
            Turn off
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
